import { FormEvent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { useAdminDraft } from "@contexts/admin/AdminDraftsProvider";
import { accountFromSearchLocation } from "@pages/AdminDashboard/accountFromSearch";
import { globalSearchKinds } from "@pages/AdminDashboard/adminRoutes";
import { orderStatusLabel } from "@pages/AdminDashboard/plainLanguageLabels";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { phoneSearchFormLayout } from "@pages/AdminDashboard/theme/phoneSearchForm";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { DataTable, EmptyStateCard } from "@pages/AdminDashboard/ui/DataTable";
import { Select } from "@pages/AdminDashboard/ui/Select";
import { TextField } from "@pages/AdminDashboard/ui/TextField";
import { searchOrders } from "@store/admin/adminAction";
import { OrderSearchState } from "@types-runway/admin";
import { OrderStatuses } from "@types-runway/case";
import { RootState } from "@types-runway/state";

import { buildOrderColumns, findOrderPatientEmail, FoundOrder } from "./orderColumns";
import {
  buildOrderSearchParams,
  describeEmptyOrderSearch,
  nextOffset,
  OrderSearchFilters,
  orderSearchPageSize,
  previousOffset,
} from "./orderSearchQuery";

const { color, radius, shadow } = adminTokens;
const [, , spaceMd, spaceLg] = adminTokens.spacing;
const barPaddingY = 14;
const orderIdMinWidth = 240;
const statusFieldWidth = 190;

const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spaceLg}px;
`;

const FilterBar = styled.form<{ $isPhone: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spaceMd}px;
  box-sizing: border-box;
  padding: ${barPaddingY}px ${spaceLg}px;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.card}px;
  box-shadow: ${shadow.card};
  ${({ $isPhone }) => $isPhone && phoneSearchFormLayout}
`;

const OrderIdSlot = styled.div`
  flex: 1;
  min-width: ${orderIdMinWidth}px;
`;

const emptyFilters: OrderSearchFilters = { orderId: "", status: "" };
// The design lists the statuses in this order.
const statusOptions = [
  { value: "", label: "Any status" },
  ...[
    OrderStatuses.NEW,
    OrderStatuses.IN_PROGRESS,
    OrderStatuses.OUT_FOR_DELIVERY,
    OrderStatuses.COMPLETED,
    OrderStatuses.CANCELLED,
    OrderStatuses.ERROR,
  ].map((status) => ({ value: status, label: orderStatusLabel(status) })),
];

const OrderSearch = () => {
  const isPhone = useIsPhoneLayout();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { status, rows } = useSelector<RootState, OrderSearchState>(
    (state) => state.admin.orderSearch
  );

  const [filters, setFilters] = useAdminDraft("order.filters", emptyFilters);
  const [searchedFilters, setSearchedFilters] = useAdminDraft(
    "order.searchedFilters",
    emptyFilters
  );
  const [offset, setOffset] = useAdminDraft("order.offset", 0);

  const isPending = status === "pending";
  const searchParams = new URLSearchParams(location.search);
  const addressEntry =
    searchParams.get("search") === globalSearchKinds.order ? searchParams.get("q") : null;

  const runSearch = (searchFilters: OrderSearchFilters, searchOffset: number) => {
    setSearchedFilters(searchFilters);
    setOffset(searchOffset);
    dispatch(searchOrders(buildOrderSearchParams(searchFilters, searchOffset)));
  };

  // ORDERS-R21: an entry from the admin search shows in the field and is searched at once; a
  // new entry arrives with a new location key, so entering the same one again searches again.
  useEffect(() => {
    if (!addressEntry) return;

    const entryFilters = { ...emptyFilters, orderId: addressEntry };
    setFilters(entryFilters);
    runSearch(entryFilters, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressEntry, location.key]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPending) return;

    runSearch(filters, 0);
  };

  const showMostRecent = () => {
    setFilters(emptyFilters);
    runSearch(emptyFilters, 0);
  };

  const goToNextPage = () => runSearch(searchedFilters, nextOffset(offset));

  const goToPreviousPage = () => {
    if (offset === 0) return;

    runSearch(searchedFilters, previousOffset(offset));
  };

  // A row opens the account of its User with the Order highlighted (FIND-R26); a row whose User
  // is not known opens nothing (FIND-R27).
  const openAccount = (order: FoundOrder) => {
    const email = findOrderPatientEmail(order);
    if (!email) return;

    history.push(
      accountFromSearchLocation(email, {
        search: "order",
        orderNumber: order.id,
        sharedOrderId: order.order_id,
      })
    );
  };

  const columns = buildOrderColumns({ onOpenAccount: openAccount });

  return (
    <Page>
      <FilterBar $isPhone={isPhone} role="search" aria-label="Order Search" onSubmit={submitSearch}>
        <OrderIdSlot>
          <TextField
            id="admin-order-search-id"
            label="Order ID or Case ID"
            placeholder="Full or first characters"
            value={filters.orderId}
            onChange={(event) => setFilters({ ...filters, orderId: event.target.value })}
          />
        </OrderIdSlot>
        <Select
          id="admin-order-search-status"
          label="Status"
          width={statusFieldWidth}
          options={statusOptions}
          value={filters.status}
          onChange={(event) => setFilters({ ...filters, status: event.target.value })}
        />
        <Button
          look="primary"
          type="submit"
          disabled={isPending}
          isPending={isPending}
          pendingLabel="Search"
        >
          Search
        </Button>
      </FilterBar>

      {status === "empty" && (
        <EmptyStateCard
          title="No Orders match"
          message={describeEmptyOrderSearch(searchedFilters)}
          actionLabel="Show most recent Orders"
          onAction={showMostRecent}
        />
      )}
      {status === "error" && (
        <Banner
          tone="error"
          title="The order search failed."
          body="Nothing changed. Try again."
          action={{ label: "Try again", onAction: () => runSearch(searchedFilters, offset) }}
        />
      )}

      {status === "found" && (
        <DataTable<FoundOrder>
          label="Orders"
          columns={columns}
          rows={rows}
          getRowKey={(order) => String(order.id)}
          onRowActivate={openAccount}
          emptyState={{ title: "No Orders match" }}
          pagination={{
            offset,
            pageSize: orderSearchPageSize,
            orderLabel: "newest first",
            onPrevious: goToPreviousPage,
            onNext: goToNextPage,
          }}
        />
      )}
    </Page>
  );
};

export default OrderSearch;
